import { FloppyDiskIcon, LockSimpleIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { useGuestSession, useResetGuestSession, type GuestSessionState } from '@entities/guest';
import { useMe } from '@entities/user/api';
import { useAuthDialog } from '@features/auth';
import { ROUTES } from '@shared/config/routes';
import { Button, Modal } from '@shared/ui';

import styles from './GuestBanner.module.css';

/** Осталось столько сообщений или меньше — счётчик выходит из тени. */
const LOW_MESSAGES = 5;

/**
 * Полоса гостевого режима (FE-205, MVP_PRD §41.2).
 *
 * Держит три вещи и ни одной сверх:
 *
 * 1. **честное обещание.** «Ваш подбор сохранён в этом браузере» — и срок.
 *    Формулировки «сохранено навсегда» здесь нет и быть не может: заказ живёт
 *    30 дней с последней активности и привязан к браузеру;
 * 2. **сохранение работы.** Кнопка ведёт в то же окно входа, что и оплата:
 *    после подтверждения кода заказ переносится в аккаунт вместе с диалогом,
 *    критериями и результатами (§41.3);
 * 3. **сброс.** Единственный способ начать второй подбор без регистрации —
 *    удалить текущий, и об этом сказано прямо, до нажатия.
 *
 * Остаток сообщений показывается **только когда он близок к концу**: счётчик
 * «29 из 30» на первом экране тревожит без причины.
 */
export function GuestBanner() {
  const me = useMe();
  const guest = useGuestSession(!me.isPending && me.data == null);

  // Решение о показе принимается здесь, а разметка — в дочернем компоненте.
  // Это не косметика: полоса зависит от окна входа, а оно существует только
  // внутри `AuthProvider`. Вызов его хука до проверки «гость ли это» сделал бы
  // оболочку `/app/*` незапускаемой везде, где гостя нет и провайдера тоже.
  if (me.data != null || guest.data == null) return null;

  return <GuestBannerBody session={guest.data} />;
}

function GuestBannerBody({ session }: { session: GuestSessionState }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { requestAuth } = useAuthDialog();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const reset = useResetGuestSession();

  const expires = new Date(session.expires_at);
  const expiresLabel = Number.isNaN(expires.getTime())
    ? null
    : new Intl.DateTimeFormat(i18n.resolvedLanguage ?? 'de', { dateStyle: 'medium' }).format(
        expires,
      );
  const low = session.messages_left <= LOW_MESSAGES;

  return (
    <aside className={styles.banner} aria-label={t('guest.banner.label')}>
      <LockSimpleIcon size={18} weight="fill" aria-hidden="true" className={styles.icon} />

      <div className={styles.text}>
        <p className={styles.title}>{t('guest.banner.title')}</p>
        <p className={styles.note}>
          {expiresLabel === null
            ? t('guest.banner.note')
            : t('guest.banner.noteUntil', { date: expiresLabel })}
        </p>
        {low ? (
          <p className={styles.messages} role="status">
            {t('guest.banner.messagesLeft', { count: session.messages_left })}
          </p>
        ) : null}
      </div>

      {/* Обе кнопки — не главное действие экрана: красная на экране одна, и
          она у самой страницы (DESIGN_SYSTEM §2.2, §5.1). Сохранение — синий
          контур, сброс — нейтральная вторичная. */}
      <div className={styles.actions}>
        <Button
          variant="outline-action"
          onClick={() => {
            requestAuth({
              type: 'navigate',
              returnUrl: `${location.pathname}${location.search}`,
            });
          }}
          iconEnd={<FloppyDiskIcon size={16} weight="regular" />}
        >
          {t('guest.banner.save')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            setConfirmOpen(true);
          }}
        >
          {t('guest.banner.reset')}
        </Button>
      </div>

      {/* Сброс необратим и удаляет диалог: подтверждение обязательно, и в нём
          сказано, что именно исчезнет (MVP_PRD §41.2). */}
      <Modal
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
        }}
        labelledBy="guest-reset-title"
      >
        <h2 id="guest-reset-title" className={styles.dialogTitle}>
          {t('guest.reset.title')}
        </h2>
        <p className={styles.dialogText}>{t('guest.reset.text')}</p>
        <div className={styles.dialogActions}>
          <Button
            variant="secondary"
            onClick={() => {
              setConfirmOpen(false);
            }}
          >
            {t('common.cancel')}
          </Button>
          {/* Удаление необратимо — деструктивная кнопка, бордовым контуром, а
              не красной заливкой главного действия (DESIGN_SYSTEM §2.4, §5.1). */}
          <Button
            variant="danger"
            disabled={reset.isPending}
            onClick={() => {
              reset.mutate(undefined, {
                onSuccess: () => {
                  setConfirmOpen(false);
                  void navigate(ROUTES.app, { replace: true });
                },
              });
            }}
          >
            {t('guest.reset.confirm')}
          </Button>
        </div>
      </Modal>
    </aside>
  );
}
